import { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import api from "./lib/api";
import { useSeo } from "./lib/seo";
import { markVisited } from "./components/dashboard/DashboardHub";
import { PublicDepartmentsIndex, PublicDepartmentState, PublicDepartment } from "./pages/PublicDepartments";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Account from "./pages/Account";
import Fitness from "./pages/Fitness";
import ResumeBuilder from "./pages/ResumeBuilder";
import AdminQuestions from "./pages/AdminQuestions";
import AdminContent from "./pages/AdminContent";
import Admin from "./pages/Admin";
import Terms from "./pages/Terms";
import FirefighterOralBoardPrep from "./pages/seo/FirefighterOralBoardPrep";
import FirefighterInterviewPractice from "./pages/seo/FirefighterInterviewPractice";
import FirefighterOralBoardQuestions from "./pages/seo/FirefighterOralBoardQuestions";
import HowToPrepareFirefighterInterview from "./pages/seo/HowToPrepareFirefighterInterview";
import FirefighterResumeExamples from "./pages/seo/FirefighterResumeExamples";
import FirefighterStationVisitQuestions from "./pages/seo/FirefighterStationVisitQuestions";
import FirefighterHiringProcess from "./pages/seo/FirefighterHiringProcess";
import FirefighterMockOralBoard from "./pages/seo/FirefighterMockOralBoard";
import FirefighterPracticeTest from "./pages/seo/FirefighterPracticeTest";
import { trackConversion } from "./lib/ads";
import { LegalFooter } from "./components/LegalFooter";
import { BillingBanner } from "./components/BillingBanner";
import Study from "./pages/Study";
import Interview from "./pages/Interview";
import Departments from "./pages/Departments";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Bookmarks from "./pages/Bookmarks";
import { Flame } from "lucide-react";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        if (data?.created_at && Date.now() - new Date(data.created_at).getTime() < 60_000) trackConversion("signup"); // brand-new Google account only
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/auth", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
      <div className="flex flex-col items-center gap-4">
        <Flame className="w-10 h-10 text-primary animate-pulse" />
        <p className="font-heading text-xl tracking-wide">Signing you in…</p>
      </div>
    </div>
  );
}

function PublicDepartmentRoute() {
  const { id } = useParams();
  return /^[A-Za-z]{2}$/.test(id) ? <PublicDepartmentState /> : <PublicDepartment />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <Flame className="w-8 h-8 text-primary animate-pulse" />
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  return <><NoIndex />{children}</>;
}

const PAGE_TITLES = [["/study/departments", "Department Research"], ["/study", "Study"], ["/interview", "Mock Oral Board"], ["/resume", "Resume Builder"], ["/fitness", "Fitness Plan"], ["/account", "Account"], ["/admin", "Admin"], ["/bookmarks", "Bookmarks"], ["/dashboard", "Dashboard"], ["/payment", "Payment"]];

function NoIndex() {
  const { pathname } = useLocation();
  const title = PAGE_TITLES.find(([p]) => pathname.startsWith(p))?.[1] || "Your account";
  useSeo({ title, noindex: true });
  return null;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    const k = [["/study/departments", null], ["/study", "study"], ["/interview", "interview"], ["/fitness", "fitness"], ["/resume", "resume"]].find(([p]) => location.pathname.startsWith(p))?.[1];
    if (k) markVisited(k);
    if (location.pathname === "/dashboard" && location.hash === "#roadmap") markVisited("roadmap");
  }, [location.pathname, location.hash]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/dashboard" element={<Protected><Study view="dashboard" /></Protected>} />
      <Route path="/study" element={<Protected><Study view="study" /></Protected>} />
      <Route path="/bookmarks" element={<Protected><Bookmarks /></Protected>} />
      <Route path="/interview" element={<Protected><Interview /></Protected>} />
      <Route path="/study/departments" element={<Protected><Departments /></Protected>} />
      <Route path="/departments/:id" element={<PublicDepartmentRoute />} />
      <Route path="/study/departments/compare" element={<Protected><Departments compare /></Protected>} />
      <Route path="/study/departments/:id" element={<Protected><Departments /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/fitness" element={<Protected><Fitness /></Protected>} />
      <Route path="/resume" element={<Protected><ResumeBuilder /></Protected>} />
      <Route path="/admin/questions" element={<Protected><AdminQuestions /></Protected>} />
      <Route path="/admin/content" element={<Protected><AdminContent /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/firefighter-oral-board-prep" element={<FirefighterOralBoardPrep />} />
      <Route path="/firefighter-interview-practice" element={<FirefighterInterviewPractice />} />
      <Route path="/firefighter-oral-board-questions" element={<FirefighterOralBoardQuestions />} />
      <Route path="/how-to-prepare-firefighter-interview" element={<HowToPrepareFirefighterInterview />} />
      <Route path="/firefighter-resume-examples" element={<FirefighterResumeExamples />} />
      <Route path="/firefighter-station-visit-questions" element={<FirefighterStationVisitQuestions />} />
      <Route path="/firefighter-hiring-process" element={<FirefighterHiringProcess />} />
      <Route path="/firefighter-mock-oral-board" element={<FirefighterMockOralBoard />} />
      <Route path="/firefighter-practice-test" element={<FirefighterPracticeTest />} />
      <Route path="/departments" element={<PublicDepartmentsIndex />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function ReferralLanding() {
  const { code } = useParams();
  useEffect(() => { if (code) sessionStorage.setItem("fireprep-ref", code.toUpperCase()); }, [code]);
  return <Navigate to={`/auth?mode=register&ref=${encodeURIComponent((code || "").toUpperCase())}`} replace />;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <BillingBanner />
          <AppRouter />
          <LegalFooter />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
